import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, renderSlot as _renderSlot, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { ref, onMounted, computed } from 'vue';
import { computedAsync } from '@vueuse/core';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import {
  useFormStore,
  IQConfigSettings,
  SettingCategory,
  useConfigSettingsStore,
  useReferenceDataStore,
  useAccountStore,
} from '@/store';
import {
  getPaymentReferenceNumberDetails,
  getPolicyOrReferenceNumber,
  getPaymentReceiptNumber,
  getSummaryPaymentAmount,
  getVisibleReceipt,
  replaceBookmarks,
  getConfirmationHeaderContent,
  getHighlights,
  getFirstPaymentContent,
  getGoodNewsDiscount,
  bindSuccessful,
  Bookmarks,
  paymentSuccessful,
  annualCreditCardPaymentFailure,
} from './confirmation-mapping';
import { useI18n } from 'vue-i18n';
import Highlights from '@/components/Highlights/Highlights.vue';
import { IHighlight } from '@/interfaces/forms/highlights-interface';
import { IFormStep, IQuote } from '@/interfaces';
import { PaymentTypes } from '@/enums/payment-types.enum';
import { PaymentFrequency } from '@/enums/payment-frequency';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { InformationBoxTypes, LinkTypes } from '@/interfaces/forms/information-box.interface';
import MembershipBenefitsBrands, {
  IBenefitsBrands,
} from '@/components/MembershipBenefitsBrands/MembershipBenefitsBrands.vue';
import { useDecisionIntelligenceStore } from '@/store/decision-intelligence/decision-intelligence.state';
import { IDecisionIntelligenceOfferResponse } from '@/interfaces/decision-intelligence/decision-intelligence-response';
import DecisionIntelligenceCrossSellOffers from '@/components/DecisionIntelligenceCrossSellOffers/DecisionIntelligenceCrossSellOffers.vue';
import ExpandableContent from '@/components/ExpandableContent/ExpandableContent.vue';
import PanelTitle from '../PanelTitle/PanelTitle.vue';
import AccountLoginInfoBox from '@/components/AccountLoginInfoBox/AccountLoginInfoBox.vue';
import AdditionalExcessesHQ from '../AdditionalExcessesHQ/AdditionalExcessesHQ.vue';
import PaymentMethods from '../PaymentMethods/PaymentMethods.vue';
import SectionPanel from '../SectionPanel/SectionPanel.vue';
import Divider from '../Divider/Divider.vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { format } from 'date-fns/format';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';

// Define props and emitters
// ------------------------------------------------------------- //

export default /*@__PURE__*/_defineComponent({
  __name: 'IQConfirmationBody',
  props: {
    step: {},
    quote: {}
  },
  setup(__props: any) {

const props = __props;

// Define Store
// ------------------------------------------------------------- //
const translation = useI18n();
const formStore = useFormStore();
const configSettingsStore = useConfigSettingsStore();
const referenceDataStore = useReferenceDataStore();
const decisionIntelligenceStore = useDecisionIntelligenceStore();
const accountStore = useAccountStore();

// Define Composables
// ------------------------------------------------------------- //
const confirmationHeaderDetails = ref<any>();

// Define Component state
// ------------------------------------------------------------- //

const showFailedAnnualPayment = computed(() => {
  return (
    props.quote.paymentType === PaymentTypes.CreditCard &&
    props.quote.paymentFrequency === PaymentFrequency.Annually &&
    !getVisibleReceipt(props.quote)
  );
});

const makeAPaymentLink = computed(() => {
  return `${props.step.formInputs?.confirmationBody?.makeAPaymentLink?.value?.href}?${replaceBookmark(Bookmarks.paymentReferenceNumber, props.step.formInputs?.confirmationBody?.makeAPaymentLink?.value?.querystring, props.quote?.paymentReferenceNumber ?? '')}`;
});

const isAuthenticated = computedAsync(async () => {
  return await accountStore.isAuthenticated;
});

const isRegisteredForSelfService = computed(() => {
  return props.quote.primaryPolicyHolderDetails?.ruleCalculations?.isRegisteredForSelfService?.value;
});

const enablePurchaseEmail = ref(false);
const enableQuoteEmail = ref(false);
const isHomeAutoRenewalPreferenceEnabled = ref(false);
const summaryPaymentAmount = ref('');
const policyOrReferenceNumber = ref('');
const receiptNumber = ref('');
const paymentReferenceNumberResult = ref<any>();

const highlights = ref<IHighlight[]>([]);
const brandDetails = ref<IBenefitsBrands>();
const paymentOrBindingFailedContent = ref<string>('');
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;
const feeByMonthResponse = ref<number>(0);

const goodNewsDiscount = ref<any>();
const firstPayment = ref<any>();

// Lifecycle hooks
// ------------------------------------------------------------- //
onMounted(async () => {
  formStore.setFormCompleted();
  // https://jira.racqgroup.local/browse/PSR-626
  // Done button disabled on previous iq payment step, need to reenable it.
  formStore.disableAllSteps = false;

  const insuranceStartDate = props.quote.insuranceStartDate?.value ?? undefined;
  const quotingFeesResponse = insuranceStartDate
    ? await referenceDataStore.getQuotingFeesPayByMonth(new Date(insuranceStartDate))
    : undefined;

  feeByMonthResponse.value = (quotingFeesResponse.value.payByTheMonthFee ?? 0) * 12;

  confirmationHeaderDetails.value = getConfirmationHeaderContent(
    props.step,
    props.quote,
    feeByMonthResponse.value,
    dictionary
  );

  goodNewsDiscount.value = getGoodNewsDiscount(props.step, props.quote, feeByMonthResponse.value, dictionary);
  firstPayment.value = getFirstPaymentContent(props.step, props.quote);

  enablePurchaseEmail.value = (await configSettingsStore.getSetting(
    IQConfigSettings.EnablePurchaseEmail,
    SettingCategory.IQSettings
  )) as boolean;
  enableQuoteEmail.value = (await configSettingsStore.getSetting(
    IQConfigSettings.EnableQuoteEmail,
    SettingCategory.IQSettings
  )) as boolean;
  isHomeAutoRenewalPreferenceEnabled.value = (await configSettingsStore.getSetting(
    IQConfigSettings.IsHomeAutoRenewalPreferenceEnabled,
    SettingCategory.IQSettings
  )) as boolean;
  await decisionIntelligenceStore.getDecisionOffer(true, false);

  summaryPaymentAmount.value = getSummaryPaymentAmount(props.step, props.quote, feeByMonthResponse.value, dictionary);

  policyOrReferenceNumber.value = getPolicyOrReferenceNumber(props.step, props.quote);
  paymentReferenceNumberResult.value = getPaymentReferenceNumberDetails(props.step, props.quote);
  receiptNumber.value = getPaymentReceiptNumber(props.step, props.quote);

  highlights.value = getHighlights(props.step, props.quote, isHomeAutoRenewalPreferenceEnabled.value);

  if (props.step.formInputs?.confirmationBody?.brandBenefits) {
    brandDetails.value = {
      brands: props.step.formInputs?.confirmationBody?.brandBenefits?.fields?.brands,
      benefitsLink: props.step.formInputs?.confirmationBody?.brandBenefits?.fields?.benefitsLink?.value,
      title: props.step.formInputs?.confirmationBody?.brandBenefits?.fields?.title?.value,
      subTitle: props.step.formInputs?.confirmationBody?.brandBenefits?.fields?.subTitle?.value,
      moreOffersText: props.step.formInputs?.confirmationBody?.brandBenefits?.fields?.moreOffersText?.value,
    };
  }

  if (showFailedAnnualPayment.value) {
    if (props.quote.policyReference) {
      paymentOrBindingFailedContent.value = replaceBookmark(
        Bookmarks.dueDate,
        props.step.formInputs?.confirmationBody?.paymentFailedAnnual?.fields?.label?.value ?? '',
        props.quote?.paymentDueDate ? format(new Date(props.quote.paymentDueDate), STANDARD_DATE_FORMAT) : ''
      );
      paymentOrBindingFailedContent.value = replaceBookmark(
        Bookmarks.paymentReferenceNumber,
        paymentOrBindingFailedContent.value ?? '',
        props.quote.paymentReferenceNumber ?? ''
      );
    }
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (goodNewsDiscount.value && goodNewsDiscount.value.show)
      ? (_openBlock(), _createBlock(InformationBox, {
          key: 0,
          type: _unref(InformationBoxTypes).SUCCESS,
          title: "",
          description: goodNewsDiscount.value?.content
        }, null, 8, ["type", "description"]))
      : _createCommentVNode("", true),
    (!_unref(bindSuccessful)(_ctx.quote) && _ctx.quote?.paymentType === _unref(PaymentTypes).PayLater && _ctx.quote?.paymentDueDate)
      ? (_openBlock(), _createBlock(InformationBox, {
          key: 1,
          type: _unref(InformationBoxTypes).WARNING,
          title: 
      _unref(replaceBookmarks)(
        _ctx.step.formInputs?.confirmationBody?.paymentDuePolicyNotBoundInformationBox?.fields?.title?.value,
        _ctx.quote,
        feeByMonthResponse.value,
        _unref(dictionary)
      )
    ,
          description: _ctx.step.formInputs?.confirmationBody?.paymentDuePolicyNotBoundInformationBox?.fields?.content?.value
        }, null, 8, ["type", "title", "description"]))
      : _createCommentVNode("", true),
    (_unref(bindSuccessful)(_ctx.quote) && _ctx.quote?.paymentType === _unref(PaymentTypes).PayLater && _ctx.quote?.paymentDueDate)
      ? (_openBlock(), _createBlock(InformationBox, {
          key: 2,
          type: _unref(InformationBoxTypes).WARNING,
          title: 
      _unref(replaceBookmarks)(
        _ctx.step.formInputs?.confirmationBody?.paymentDuePolicyBoundInformationBox?.fields?.title?.value,
        _ctx.quote,
        feeByMonthResponse.value,
        _unref(dictionary)
      )
    ,
          description: _ctx.step.formInputs?.confirmationBody?.paymentDuePolicyBoundInformationBox?.fields?.content?.value
        }, null, 8, ["type", "title", "description"]))
      : _createCommentVNode("", true),
    (firstPayment.value && firstPayment.value.show)
      ? (_openBlock(), _createBlock(InformationBox, {
          key: 3,
          type: _unref(InformationBoxTypes).INFO,
          title: "",
          description: firstPayment.value.content
        }, null, 8, ["type", "description"]))
      : _createCommentVNode("", true),
    (_unref(annualCreditCardPaymentFailure)(_ctx.quote) && paymentOrBindingFailedContent.value)
      ? (_openBlock(), _createBlock(InformationBox, {
          key: 4,
          type: _unref(InformationBoxTypes).WARNING,
          description: paymentOrBindingFailedContent.value,
          links: [
      {
        title: 'Pay now',
        type: _unref(LinkTypes).BUTTON_PRIMARY,
        url: makeAPaymentLink.value,
      },
    ]
        }, null, 8, ["type", "description", "links"]))
      : _createCommentVNode("", true),
    (
      _ctx.quote?.paymentFrequency === _unref(PaymentFrequency).Annually &&
      _ctx.quote?.paymentType === _unref(PaymentTypes).CreditCard &&
      !_unref(bindSuccessful)(_ctx.quote)
    )
      ? (_openBlock(), _createBlock(InformationBox, {
          key: 5,
          type: _unref(InformationBoxTypes).WARNING,
          description: _ctx.step.formInputs?.confirmationBody?.paymentDueNoPaymentInformationBox?.fields?.content?.value
        }, null, 8, ["type", "description"]))
      : (_unref(annualCreditCardPaymentFailure)(_ctx.quote))
        ? (_openBlock(), _createBlock(InformationBox, {
            key: 6,
            type: _unref(InformationBoxTypes).WARNING,
            description: _ctx.step.formInputs?.confirmationBody?.paymentFailedSecondaryAnnual?.fields?.label?.value
          }, null, 8, ["type", "description"]))
        : _createCommentVNode("", true),
    _cache[0] || (_cache[0] = _createElementVNode("div", { class: "border-b my-2 bg-racq-white" }, null, -1)),
    _createVNode(Highlights, {
      showNumber: true,
      highlights: highlights.value,
      highlightsTitle: _ctx.step.formInputs?.confirmationBody?.highlightsTitle?.fields?.label?.value
    }, null, 8, ["highlights", "highlightsTitle"]),
    (
      _ctx.quote.paymentType === _unref(PaymentTypes).PayLater || (_unref(annualCreditCardPaymentFailure)(_ctx.quote) && _unref(bindSuccessful)(_ctx.quote))
    )
      ? (_openBlock(), _createBlock(SectionPanel, { key: 7 }, {
          sectionPanelContent: _withCtx(() => [
            _createVNode(PanelTitle, _mergeProps(
          _ctx.quote.paymentType === _unref(PaymentTypes).PayLater
            ? _ctx.step.formInputs?.confirmationBody?.paymentMethodsTitle?.fields
            : _ctx.step.formInputs?.confirmationBody?.paymentMethodsAltTitle?.fields
        , { class: "mb-6" }), null, 16),
            _createVNode(PaymentMethods, {
              paymentReferenceNumber: paymentReferenceNumberResult.value?.content,
              paymentMethods: _ctx.step.formInputs?.confirmationBody?.paymentMethods?.fields?.paymentMethods,
              bpayBillerCode: 
          _ctx.step.formInputs?.confirmationBody?.paymentMethods?.fields?.bpayBillerCode?.fields?.content?.value
        ,
              showAdditionalContent: true,
              filterPaymentMethods: true,
              bindSuccessful: (_unref(bindSuccessful)(_ctx.quote) as boolean) ?? false
            }, null, 8, ["paymentReferenceNumber", "paymentMethods", "bpayBillerCode", "bindSuccessful"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (brandDetails.value)
      ? (_openBlock(), _createBlock(MembershipBenefitsBrands, {
          key: 8,
          step: _ctx.step,
          quote: _ctx.quote,
          brandDetails: brandDetails.value
        }, null, 8, ["step", "quote", "brandDetails"]))
      : _createCommentVNode("", true),
    _createVNode(Divider),
    (
      _unref(decisionIntelligenceStore).decisionOfferCrossSellResponse?.propositions != null &&
      _unref(decisionIntelligenceStore).decisionOfferCrossSellResponse?.propositions.length
    )
      ? (_openBlock(), _createBlock(DecisionIntelligenceCrossSellOffers, {
          key: 9,
          quote: _ctx.quote,
          decisionOffer: _unref(decisionIntelligenceStore).decisionOfferCrossSellResponse as IDecisionIntelligenceOfferResponse,
          stepTitle: _ctx.step.stepTitle
        }, null, 8, ["quote", "decisionOffer", "stepTitle"]))
      : _createCommentVNode("", true),
    _renderSlot(_ctx.$slots, "reviewSummary"),
    (_ctx.step.formInputs?.confirmationBody.importantInformationTitle?.value)
      ? (_openBlock(), _createBlock(PanelTitle, {
          key: 10,
          title: _ctx.step.formInputs?.confirmationBody.importantInformationTitle,
          subTitle: _ctx.step.formInputs?.confirmationBody.importantInformation
        }, null, 8, ["title", "subTitle"]))
      : _createCommentVNode("", true),
    (_ctx.step.formInputs?.confirmationBody.importantInformationTitle?.value)
      ? (_openBlock(), _createBlock(Divider, { key: 11 }))
      : _createCommentVNode("", true),
    (_ctx.step.formInputs?.confirmationBody.preExistingConditionWaivers?.value)
      ? (_openBlock(), _createBlock(PanelTitle, {
          key: 12,
          title: _ctx.step.formInputs?.confirmationBody.preExistingConditionWaiversTitle,
          subTitle: _ctx.step.formInputs?.confirmationBody.preExistingConditionWaivers
        }, null, 8, ["title", "subTitle"]))
      : _createCommentVNode("", true),
    (_ctx.step.formInputs?.confirmationBody.preExistingConditionWaivers?.value)
      ? (_openBlock(), _createBlock(Divider, { key: 13 }))
      : _createCommentVNode("", true),
    (_ctx.step.formInputs?.confirmationBody?.additionalExcess)
      ? (_openBlock(), _createBlock(ExpandableContent, {
          key: 14,
          id: "excesses_details",
          toggleButtonExpandText: _ctx.step.formInputs?.confirmationBody?.additionalExcess?.toggleButtonExpandText?.value,
          toggleButtonCollapseText: _ctx.step.formInputs?.confirmationBody?.additionalExcess?.toggleButtonCollapseText?.value,
          dynamicExpander: true,
          class: "input-spacing"
        }, {
          introContent: _withCtx(() => [
            _createVNode(PanelTitle, _normalizeProps(_guardReactiveProps(_ctx.step.formInputs?.confirmationBody?.additionalExcessesTitle?.fields)), null, 16)
          ]),
          conditionalContent: _withCtx(() => [
            _createVNode(AdditionalExcessesHQ, {
              fields: _ctx.step.formInputs?.confirmationBody?.additionalExcesses?.fields,
              quote: _ctx.quote,
              stepId: _ctx.step.id!
            }, null, 8, ["fields", "quote", "stepId"])
          ]),
          _: 1
        }, 8, ["toggleButtonExpandText", "toggleButtonCollapseText"]))
      : _createCommentVNode("", true),
    (_ctx.step.formInputs?.confirmationBody?.coolingOffPeriod)
      ? (_openBlock(), _createBlock(ExpandableContent, {
          key: 15,
          toggleButtonExpandText: _ctx.step.formInputs?.confirmationBody?.coolingOffPeriod?.fields?.toggleButtonExpandText?.value,
          toggleButtonCollapseText: 
      _ctx.step.formInputs?.confirmationBody?.coolingOffPeriod?.fields?.toggleButtonCollapseText?.value
    ,
          dynamicExpander: true
        }, {
          introContent: _withCtx(() => [
            _createVNode(PanelTitle, _normalizeProps(_guardReactiveProps(_ctx.step.formInputs?.confirmationBody?.coolingOffPeriodTitle?.fields)), null, 16)
          ]),
          conditionalContent: _withCtx(() => [
            _createVNode(_unref(RichText), {
              field: {
          value: _ctx.step.formInputs?.confirmationBody?.coolingOffPeriod?.fields?.conditionalContent?.value,
        },
              class: "richtext"
            }, null, 8, ["field"])
          ]),
          _: 1
        }, 8, ["toggleButtonExpandText", "toggleButtonCollapseText"]))
      : _createCommentVNode("", true),
    (isRegisteredForSelfService.value && !_unref(isAuthenticated))
      ? (_openBlock(), _createBlock(AccountLoginInfoBox, {
          key: 16,
          fields: _ctx.step.formInputs?.confirmationBody?.selfServiceAccountLogin?.fields
        }, null, 8, ["fields"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})